import React, { useState, useEffect } from 'react';
import {
  TextField,
  Button,
  Grid,
  Container,
  Snackbar,
  Paper,
  CircularProgress,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Typography,
  Box,
  FormHelperText
} from '@mui/material';
import MuiAlert from '@mui/material/Alert';
import { InputAdornment } from '@mui/material';
import {IconButton } from '@mui/material';
import {Tooltip } from '@mui/material';
import { Info } from '@mui/icons-material'
import apiService from '../components/ReasuableFuncations/api';

const currencyOptions = [
  { symbol: '$', code: 'USD', name: 'US Dollar' },
  { symbol: '€', code: 'EUR', name: 'Euro' },
  { symbol: '£', code: 'GBP', name: 'British Pound' },
  { symbol: '₹', code: 'INR', name: 'Indian Rupee' },
  { symbol: 'R', code: 'ZAR', name: 'South African Rand' },
  { symbol: 'KSh', code: 'KES', name: 'Kenyan Shilling' },
  { symbol: 'USh', code: 'UGX', name: 'Ugandan Shilling' },
  { symbol: 'TSh', code: 'TZS', name: 'Tanzanian Shilling' },
  { symbol: 'ETB', code: 'ETB', name: 'Ethiopian Birr' },
];


const conversionRates = {
  USD: 1,
  EUR: 1.08,
  GBP: 1.30,
  INR: 0.012,
  ZAR: 0.057,
  KES: 0.0077,
  UGX: 0.00027,
  TZS: 0.00037,
  ETB: 0.0082
};


const convertToUSD = (amount, fromCurrency) => {
  const rate = conversionRates[fromCurrency];
  if (!rate) return amount; 
  return amount * rate;
};

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const BudgetForm = () => {
  const [projects, setProjects] = useState([]);
  const [formData, setFormData] = useState({
    selectedProjectId: '',
    expectedBudget: {
      value: '',
      currency: 'ZAR',
      symbol: 'R'
    },
    actualBudget: {
      value: '',
      currency: 'ZAR',
      symbol: 'R'
    },
    comment: '',
    budgetDate: '',
  });
  const [currency, setCurrency] = useState('ZAR');
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  const getCurrencySymbol = (code) => {
    const currency = currencyOptions.find(c => c.code === code);
    return currency ? currency.symbol : '';
  };

  useEffect(() => {
    fetchProjects();
  }, []);

  useEffect(() => {
    // Scroll to the top of the page when the component mounts
    window.scrollTo(0, 0);
  }, []);

  const handleCurrencyChange = (e) => {
    const newCurrency = e.target.value;
    const newSymbol = getCurrencySymbol(newCurrency);
    setCurrency(newCurrency);

    
    setFormData(prev => ({
      ...prev,
      expectedBudget: {
        ...prev.expectedBudget,
        currency: newCurrency,
        symbol: newSymbol
      },
      actualBudget: {
        ...prev.actualBudget,
        currency: newCurrency,
        symbol: newSymbol
      }
    }));
  };

  const validateNumberInput = (e) => {
    const value = e.target.value;
    if (!/^\d*\.?\d*$/.test(value)) { // Only allow digits and one decimal point
      e.target.value = value.replace(/[^\d.]/g, ''); // Remove non-numeric chars except decimal
      return false;
    }
    return true;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
   
    const isMonetaryField = ['expectedBudget', 'actualBudget'].includes(name);
    
    if (isMonetaryField) {
      setFormData((prevData) => ({
        ...prevData,
        [name]: {
          value: validateNumberInput(e) ? value : prevData[name].value,
          currency: currency,
          symbol: getCurrencySymbol(currency)
        }
      }));
    } else {
      setFormData((prevData) => ({ ...prevData, [name]: value }));
    }
  };

  const fetchProjects = async () => {
    try {
      const userData = JSON.parse(localStorage.getItem('user'));
        if (!userData) {
        console.error('User data not found. Redirecting to login.');
        return;
        }
      const { selectedOption, regionalNetworkOption, memberStateOption, countryOption } = userData;
      
      let url = '/api/projects-stake';
      let response;
      if (selectedOption === 'Comesa') {
        response = await apiService.get('api/project-data');
      } else if (selectedOption === 'Regional Network' && regionalNetworkOption) {
        response = await apiService.get(`${url}?regionalNetwork=${regionalNetworkOption}`);
      } else if (selectedOption === 'Member State' && memberStateOption) {
        response = await apiService.get(`${url}?country=${countryOption}`);
      }

      if (response.statusText !== 'OK') throw new Error('Failed to fetch projects');
      
      const data = response.data;
      setProjects(data);
    } catch (error) {
      showSnackbar('Error fetching projects: ' + error.message, 'error');
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.selectedProjectId) newErrors.selectedProjectId = 'Project is required';
    if (!formData.expectedBudget.value || isNaN(formData.expectedBudget.value) || Number(formData.expectedBudget.value) <= 0) {
      newErrors.expectedBudget = 'Expected Budget must be a positive number';
    }
    if (!formData.actualBudget.value || isNaN(formData.actualBudget.value) || Number(formData.actualBudget.value) <= 0) {
      newErrors.actualBudget = 'Actual Budget must be a positive number';
    }
    if (!formData.budgetDate) newErrors.budgetDate = 'Budget Date is required';
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = validateForm();
  
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
  
    setLoading(true);
    try {
      const userData = JSON.parse(localStorage.getItem('user'));
      if (!userData) {
        console.error('User data not found. Redirecting to login.');
        return;
      }
      
      const expectedBudgetUSD = convertToUSD(
        Number(formData.expectedBudget.value),
        formData.expectedBudget.currency
      );
      
      const actualBudgetUSD = convertToUSD(
        Number(formData.actualBudget.value),
        formData.actualBudget.currency
      );
  
      const response = await apiService.post('/api/budgets', {
          project: formData.selectedProjectId,
          expectedBudget: expectedBudgetUSD, 
          actualBudget: actualBudgetUSD, 
          comment: formData.comment,
          budgetDate: formData.budgetDate,
          originalCurrency: currency, 
          ...userData
        });
  
      if (response.statusText !== 'Created') {
        const error =  response.data;
        throw new Error(error.message || 'Failed to create budget');
      }
  
      showSnackbar('Budget created successfully!');
      
      setFormData({
        selectedProjectId: '',
        expectedBudget: {
          value: '',
          currency: currency,
          symbol: getCurrencySymbol(currency)
        },
        actualBudget: {
          value: '',
          currency: currency,
          symbol: getCurrencySymbol(currency)
        },
        comment: '',
        budgetDate: '',
      });
      setErrors({});
    } catch (error) {
      showSnackbar(error.message, 'error');
    } finally {
      setLoading(false);
    }
  };
  
  
  

  return (
    <Container maxWidth="md">
      <Paper elevation={3} sx={{ p: 4, mt: 4 }}>
        <Typography variant="h5" gutterBottom>
          Create Budget
        </Typography>

        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <FormControl fullWidth error={!!errors.selectedProjectId}>
                <InputLabel>Project Name*</InputLabel>
                <Select
                  name="selectedProjectId"
                  value={formData.selectedProjectId}
                  onChange={handleInputChange}
                  label="Project Name*"
                >
                  {projects.map((project) => (
                    <MenuItem key={project._id} value={project._id}>
                      {project.projectName}
                    </MenuItem>
                  ))}
                </Select>
                {errors.selectedProjectId && (
                  <FormHelperText>{errors.selectedProjectId}</FormHelperText>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Currency</InputLabel>
                <Select
                  value={currency}
                  onChange={handleCurrencyChange}
                  label="Currency"
                >
                  {currencyOptions.map((curr) => (
                    <MenuItem key={curr.code} value={curr.code}>
                      {`${curr.symbol} - ${curr.name}`}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                name="expectedBudget"
                label="Expected Budget*"
                type="text" // Changed from number to text
                InputProps={{
                  startAdornment: (
                  <Box sx={{ mr: 1 }}>{formData.expectedBudget.symbol}</Box>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip title="Enter the Expected Budget in your chosen currency">
                        <IconButton edge="end">
                          <Info />
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  ),
                }}
                value={formData.expectedBudget.value}
                onChange={handleInputChange}
                error={!!errors.expectedBudget}
                helperText={errors.expectedBudget || "Enter numbers only"}
                onKeyPress={(e) => {
                  if (!/[0-9.]/.test(e.key)) {
                    e.preventDefault();
                  }
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                name="actualBudget"
                label="Actual Budget*"
                type="text" // Changed from number to text
                InputProps={{
                  startAdornment:(
                     <Box sx={{ mr: 1 }}>{formData.actualBudget.symbol}</Box>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip title="Enter the Actual Budget in your chosen currency">
                        <IconButton edge="end">
                          <Info />
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  ),
                }}
                value={formData.actualBudget.value}
                onChange={handleInputChange}
                error={!!errors.actualBudget}
                helperText={errors.actualBudget || "Enter numbers only"}
                onKeyPress={(e) => {
                  if (!/[0-9.]/.test(e.key)) {
                    e.preventDefault();
                  }
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                name="comment"
                label="Comment"
                multiline
                rows={2}
                value={formData.comment}
                onChange={handleInputChange}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                name="budgetDate"
                label="Budget Date*"
                type="date"
                value={formData.budgetDate}
                onChange={handleInputChange}
                error={!!errors.budgetDate}
                helperText={errors.budgetDate}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                fullWidth
                disabled={loading}
                startIcon={loading && <CircularProgress size={20} color="inherit" />}
              >
                {loading ? 'Creating Budget...' : 'Create Budget'}
              </Button>
            </Grid>
          </Grid>
        </form>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        >
          <Alert severity={snackbar.severity} onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Paper>
    </Container>
  );
};

export default BudgetForm;